import { useState } from 'react';
import { Users, Plus, ShieldAlert, AlertCircle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { UserList } from '../../components/users/UserList';
import { UserForm } from '../../components/users/UserForm';
import { UserSessions } from '../../components/users/UserSessions';
import { UserStatusModal } from '../../components/users/UserStatusModal';
import { Modal } from '../../components/ui/Modal';
import { useUsers, type UserWithDetails } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';

export function UsersPage() {
  const { users, currentUser, loading, error, addUser, updateUser, toggleUserStatus } = useUsers();
  const { can, isAdmin, roleLabel } = usePermissions();
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [showSessions, setShowSessions] = useState(false);
  const [userToToggle, setUserToToggle] = useState<UserWithDetails | null>(null);

  // Debug log
  console.log('👥 UsersPage render:', { usersCount: users.length, currentUserId: currentUser?.id, loading, error });

  // Vérifier l'accès à la page - basé sur le rôle admin ou permission manage_users
  const canManageUsers = can('manage_users') || isAdmin;

  // Accès refusé si pas admin et pas de permission
  if (!canManageUsers) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
        <div className="card p-8 text-center">
          <ShieldAlert className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Accès restreint</h2>
          <p className="text-muted-foreground">
            En tant que <strong>{roleLabel}</strong>, vous n'avez pas accès à la gestion des utilisateurs.
          </p>
        </div>
      </div>
    );
  }

  const handleEditUser = (user: UserWithDetails) => {
    setSelectedUser(user);
  };

  const handleViewSessions = (user: UserWithDetails) => {
    setSelectedUser(user);
    setShowSessions(true);
  };

  const handleToggleStatus = async (user: UserWithDetails) => {
    setUserToToggle(user);
  };

  const handleConfirmToggleStatus = async () => {
    if (userToToggle) {
      await toggleUserStatus(userToToggle.id, userToToggle.status);
      setUserToToggle(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => setShowNewUserModal(true)}
        >
          Nouvel Utilisateur
        </Button>
      </div>

      <Card title="Liste des utilisateurs" icon={Users}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <UserList
          users={users}
          currentUserId={currentUser?.id}
          onEdit={handleEditUser}
          onToggleStatus={handleToggleStatus}
          onViewSessions={handleViewSessions}
          loading={loading}
        />
      </Card>

      <Modal
        isOpen={showNewUserModal}
        onClose={() => setShowNewUserModal(false)}
        title="Nouvel Utilisateur"
      >
        <UserForm
          onSubmit={async (data) => {
            await addUser(data);
            setShowNewUserModal(false);
          }}
          onCancel={() => setShowNewUserModal(false)}
        />
      </Modal>

      {selectedUser && (
        <Modal
          isOpen={!!selectedUser && !showSessions}
          onClose={() => setSelectedUser(null)}
          title="Modifier l'utilisateur"
        >
          <UserForm
            user={selectedUser}
            onSubmit={async (data) => {
              await updateUser(selectedUser.id, data);
              setSelectedUser(null);
            }}
            onCancel={() => setSelectedUser(null)}
          />
        </Modal>
      )}

      {selectedUser && (
        <Modal
          isOpen={showSessions}
          onClose={() => {
            setShowSessions(false);
            setSelectedUser(null);
          }}
          title={`Sessions - ${selectedUser.name}`}
        >
          <UserSessions userId={selectedUser.id} />
        </Modal>
      )}

      {userToToggle && (
        <UserStatusModal
          isOpen={!!userToToggle}
          onClose={() => setUserToToggle(null)}
          onConfirm={handleConfirmToggleStatus}
          user={userToToggle}
        />
      )}
    </div>
  );
}